<script setup lang="ts">
import { saveAs } from 'file-saver'
import VChart from 'vue-echarts'
import { MapboxMap, MapboxLayer, MapboxMarker } from '@studiometa/vue-mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import type { ECOption } from '@/plugins/echarts'
import { notifyError } from '@/utils/helpers'

definePage({
  meta: {
    id: 'prsm.meteo',
    icon: 'mdi-weather-partly-cloudy',
    title: 'Données météo',
    drawerIndex: 80,
    requiresAuth: true,
    requiresSiteAccess: 'prisme.base'
  },
})

const theme = useTheme()
const utilisateurStore = useUtilisateurStore()
const site = ref(utilisateurStore.siteSélectionné)
const isLoading = ref(false)
const startDate = ref('')
const endDate = ref('')
const latitude = ref(site.value.latitudeGoogleMaps)
const longitude = ref(site.value.longitudeGoogleMaps)
const markerPos = computed(() => [longitude.value, latitude.value] as [number, number])
const mapInitialCenter = [longitude.value, latitude.value] as [number, number]
const accessToken = 'pk.eyJ1IjoiYWdyaW1ldHJpZSIsImEiOiJjbTc3cjhrMTgxZGNqMnFxOGM4aGpjaW5hIn0.fUMzok-3aNnM9Zg7hpBKjQ'
const mapRef = ref<InstanceType<typeof MapboxMap> | null>(null)

// ── Field polygons ────────────────────────────────────────────────────────
const showChamps = ref(true)
const selectedProducers = ref<string[]>([])

const champsData = ref({ type: 'FeatureCollection', features: [] } as any)

const producerColors = [
  '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
  '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FF5722',
  '#009688', '#673AB7', '#CDDC39', '#795548', '#607D8B',
  '#FFC107', '#03A9F4', '#76FF03', '#FF4081', '#536DFE',
]

const producerColorMap = computed(() => {
  const unique = [...new Set(
    champsData.value.features
      .map((f: any) => f.properties?.fast_carto)
      .filter((v: any) => v != null)
  )] as string[]
  const map = new Map<string, string>()
  unique.forEach((p, i) => map.set(p, producerColors[i % producerColors.length]))
  return map
})

const champsLabelsData = computed(() => {
  const features = champsData.value.features.map((feature: any) => {
    const coords = feature.geometry?.coordinates
    if (!coords?.length) return null
    const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
    if (!ring?.length) return null
    let sumX = 0, sumY = 0
    for (const p of ring) { sumX += p[0]; sumY += p[1] }
    const label = feature.properties?.IDENCHAMP || ''
    if (!label) return null
    return { type: 'Feature', geometry: { type: 'Point', coordinates: [sumX / ring.length, sumY / ring.length] }, properties: { IDENCHAMP: label } }
  }).filter(Boolean)
  return { type: 'FeatureCollection', features }
})

const champsGroupLabelsData = computed(() => {
  const groups = new Map<string, number[][]>()
  for (const feature of champsData.value.features) {
    const producer = feature.properties?.fast_carto
    if (!producer) continue
    const coords = feature.geometry?.coordinates
    if (!coords?.length) continue
    const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
    if (!ring?.length) continue
    let sumX = 0, sumY = 0
    for (const p of ring) { sumX += p[0]; sumY += p[1] }
    if (!groups.has(producer)) groups.set(producer, [])
    groups.get(producer)!.push([sumX / ring.length, sumY / ring.length])
  }
  const features = [...groups.entries()].map(([producer, centroids]) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [
      centroids.reduce((s, c) => s + c[0], 0) / centroids.length,
      centroids.reduce((s, c) => s + c[1], 0) / centroids.length,
    ]},
    properties: { fast_carto: producer },
  }))
  return { type: 'FeatureCollection', features }
})

function buildFillColorExpression() {
  const entries = [...producerColorMap.value.entries()]
  if (entries.length === 0) return '#FFEB3B'
  const expr: any[] = ['match', ['get', 'fast_carto']]
  for (const [key, color] of entries) expr.push(key, color)
  expr.push('#FFEB3B')
  return expr
}

function buildOutlineColorExpression() {
  const entries = [...producerColorMap.value.entries()]
  if (entries.length === 0) return '#F57F17'
  const expr: any[] = ['match', ['get', 'fast_carto']]
  for (const [key, color] of entries) expr.push(key, color)
  expr.push('#F57F17')
  return expr
}

const champsLayerOptions = computed(() => ({
  type: 'fill',
  source: { type: 'geojson', data: champsData.value },
  layout: { visibility: showChamps.value ? 'visible' : 'none' },
  paint: { 'fill-color': buildFillColorExpression(), 'fill-opacity': 0.35, 'fill-outline-color': buildOutlineColorExpression() },
}))

const champsOutlineLayerOptions = computed(() => ({
  type: 'line',
  source: { type: 'geojson', data: champsData.value },
  layout: { visibility: showChamps.value ? 'visible' : 'none' },
  paint: { 'line-color': buildOutlineColorExpression(), 'line-width': 2 },
}))

const champsLabelLayerOptions = computed(() => ({
  type: 'symbol',
  source: { type: 'geojson', data: champsLabelsData.value },
  minzoom: 11,
  layout: {
    'text-field': ['get', 'IDENCHAMP'],
    'text-size': 12,
    'text-anchor': 'center',
    'text-allow-overlap': false,
    'text-ignore-placement': false,
    'text-optional': true,
    visibility: showChamps.value ? 'visible' : 'none',
  },
  paint: { 'text-color': '#000000', 'text-halo-color': '#FFFFFF', 'text-halo-width': 1.5 },
}))

const champsGroupLabelLayerOptions = computed(() => {
  const entries = [...producerColorMap.value.entries()]
  let textColor: any = '#FFFFFF'
  if (entries.length > 0) {
    textColor = ['match', ['get', 'fast_carto']]
    for (const [key, color] of entries) textColor.push(key, color)
    textColor.push('#FFFFFF')
  }
  return {
    type: 'symbol',
    source: { type: 'geojson', data: champsGroupLabelsData.value },
    layout: {
      'text-field': ['get', 'fast_carto'],
      'text-size': 15,
      'text-anchor': 'center',
      'text-allow-overlap': false,
      'text-optional': true,
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-letter-spacing': 0.05,
      visibility: showChamps.value ? 'visible' : 'none',
    },
    paint: { 'text-color': textColor, 'text-halo-color': 'rgba(0,0,0,0.75)', 'text-halo-width': 2 },
  }
})

const champsFilterExpression = computed(() => {
  const all = [...producerColorMap.value.keys()]
  if (all.length === 0 || selectedProducers.value.length === all.length) return null
  if (selectedProducers.value.length === 0) return ['boolean', false]
  return ['in', ['get', 'fast_carto'], ['literal', selectedProducers.value]]
})

watch(selectedProducers, () => {
  const map = mapRef.value?.map
  if (!map) return
  const filter = champsFilterExpression.value
  for (const id of ['champs-polygons', 'champs-outlines', 'champs-labels', 'champs-group-labels']) {
    if (map.getLayer(id)) map.setFilter(id, filter as any)
  }
}, { deep: true })

async function getGeoJson() {
  try {
    const siteCS = utilisateurStore.siteSélectionné.lettresDappel
    const result = await apiProphet.get(`prsm/${siteCS}/champs/geojson`)
    const jsons = result.data as any[]
    const allFeatures: any[] = []
    for (const geojson of jsons) {
      if (geojson.type === 'FeatureCollection' && geojson.features) allFeatures.push(...geojson.features)
      else if (geojson.type === 'Feature') allFeatures.push(geojson)
    }
    champsData.value = { type: 'FeatureCollection', features: allFeatures }
    selectedProducers.value = [...producerColorMap.value.keys()]

    const map = mapRef.value?.map
    if (map) {
      for (const [src, data] of [
        ['champs-polygons', champsData.value],
        ['champs-outlines', champsData.value],
        ['champs-labels',   champsLabelsData.value],
        ['champs-group-labels', champsGroupLabelsData.value],
      ] as [string, any][]) {
        if (map.getSource(src)) (map.getSource(src) as any).setData(data)
      }
      if (map.getLayer('champs-polygons')) {
        map.setPaintProperty('champs-polygons', 'fill-color', buildFillColorExpression())
        map.setPaintProperty('champs-polygons', 'fill-outline-color', buildOutlineColorExpression())
      }
      if (map.getLayer('champs-outlines'))
        map.setPaintProperty('champs-outlines', 'line-color', buildOutlineColorExpression())
    }
  } catch (error) {
    const status = (error as any)?.response?.status
    const requestUrl = String((error as any)?.config?.url ?? '')
    const isChampsJson404 =
      status === 404
      && /prsm\/[^/]+\/champs\/(json|geojson)/i.test(requestUrl)

    if (!isChampsJson404)
      notifyError(error)
  }
}

onMounted(() => { getGeoJson() })

function onMapClick(e: any) {
  latitude.value  = Math.round(e.lngLat.lat * 1e6) / 1e6
  longitude.value = Math.round(e.lngLat.lng * 1e6) / 1e6
}

const headers = [
  { title: 'Date', key: 'date' },
  { title: 'Température moy. (°C)', key: 'temperature' },
  { title: 'Température min (°C)', key: 'minTemperature' },
  { title: 'Température max (°C)', key: 'maxTemperature' },
  { title: 'Précipitations (mm)', key: 'rainfall' },
]

declare interface WeatherRow {
  date: string
  temperature: number
  minTemperature: number
  maxTemperature: number
  rainfall: number
}

const items = ref<WeatherRow[]>([])

async function loadHistoricalData() {
  isLoading.value = true
  items.value = []
  try {
    const config = {
      params: {
        latitude: latitude.value,
        longitude: longitude.value,
        startDate: startDate.value,
        endDate: endDate.value,
      },
    }
    const response = await apiOmni.get('/api/azuremaps/weather/historical/actuals/daily', config)

    if (response.data.results &&
        Array.isArray(response.data.results) &&
        response.data.results.length > 0) {
      items.value = response.data.results.map((result: any) => ({
        date: result.date,
        temperature: result.temperature.average.value,
        minTemperature: result.temperature.minimum.value,
        maxTemperature: result.temperature.maximum.value,
        rainfall: result.precipitation.value,
      }))
      updateChart()
    }
  } catch (error) {
    console.error('Error loading weather:', error)
  } finally {
    isLoading.value = false
  }
}

function updateChart() {
  chartOptions.value = {
    ...chartOptions.value,
    xAxis: {
      ...(chartOptions.value.xAxis as object),
      data: items.value.map(item => item.date),
    },
    series: [
      {
        name: 'Température moy.',
        type: 'line',
        data: items.value.map(item => item.temperature),
        yAxisIndex: 0,
        showSymbol: false,
        smooth: true,
        lineStyle: { width: 2 },
      },
      {
        name: 'Précipitations',
        type: 'bar',
        data: items.value.map(item => item.rainfall),
        yAxisIndex: 1,
        barMaxWidth: 14,
      },
    ],
  }
}

const chartOptions = ref<ECOption>({
  backgroundColor: 'transparent',
  tooltip: { trigger: 'axis' },
  legend: { data: ['Température moy.', 'Précipitations'] },
  grid: { top: 44, left: '2%', right: '3%', bottom: '3%', containLabel: true },
  xAxis: {
    type: 'category',
    boundaryGap: true,
    data: [],
  },
  yAxis: [
    { type: 'value', name: 'Température (°C)', position: 'left' },
    { type: 'value', name: 'Précipitations (mm)', position: 'right', min: 0 },
  ],
  series: [
    { name: 'Température moy.', type: 'line', data: [], yAxisIndex: 0, showSymbol: false, smooth: true, lineStyle: { width: 2 } },
    { name: 'Précipitations',   type: 'bar',  data: [], yAxisIndex: 1, barMaxWidth: 14 },
  ],
})

const exportCSV = () => {
  if (items.value.length === 0) return
  const csvContent = [
    'Date,Température moy. (°C),Température min (°C),Température max (°C),Précipitations (mm)',
    ...items.value.map(i =>
      `${i.date},${i.temperature},${i.minTemperature},${i.maxTemperature},${i.rainfall}`
    ),
  ].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  saveAs(blob, `données_météo_${startDate.value}_${endDate.value}.csv`)
}
</script>

<template>
  <v-card class="mx-4 my-4" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-4">
      <div class="d-flex flex-wrap" style="gap: 16px;">

        <!-- Left: title + controls -->
        <div class="d-flex flex-column" style="gap: 14px; flex: 1 1 280px; min-width: 0;">
          <div class="d-flex align-center" style="gap: 12px;">
            <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
              <v-icon icon="mdi-weather-partly-cloudy" size="26"/>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Données météo historiques</div>
              <div class="text-caption text-medium-emphasis">Températures &amp; précipitations journalières</div>
            </div>
          </div>

          <!-- Date controls -->
          <div class="d-flex flex-wrap align-center" style="gap: 10px;">
            <v-text-field
              v-model="startDate"
              label="Date de début"
              type="date"
              variant="outlined"
              density="compact"
              rounded="lg"
              hide-details
              style="min-width: 160px; flex: 1 1 160px;"
            />
            <v-text-field
              v-model="endDate"
              label="Date de fin"
              type="date"
              variant="outlined"
              density="compact"
              rounded="lg"
              hide-details
              style="min-width: 160px; flex: 1 1 160px;"
            />
            <v-btn
              color="primary"
              variant="tonal"
              rounded="pill"
              :loading="isLoading"
              prepend-icon="mdi-download-network"
              @click="loadHistoricalData">
              Charger
            </v-btn>
          </div>

          <!-- Coordinate display -->
          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
            <v-icon icon="mdi-map-marker-outline" size="16" color="info" class="me-1"/>
            <v-chip size="small" color="info" variant="tonal" label>
              Lat&nbsp;<strong>{{ latitude }}</strong>
            </v-chip>
            <v-chip size="small" color="info" variant="tonal" label>
              Lng&nbsp;<strong>{{ longitude }}</strong>
            </v-chip>
            <span class="text-caption text-medium-emphasis ms-1">
              Cliquez sur la carte pour changer la position
            </span>
          </div>
        </div>

        <!-- Right: place-picker map -->
        <div style="flex: 1 1 340px; min-width: 260px; position: relative;">
          <div class="map-wrapper">
            <MapboxMap
              ref="mapRef"
              style="width: 100%; height: 100%;"
              :center="mapInitialCenter"
              :access-token="accessToken"
              map-style="mapbox://styles/mapbox/satellite-streets-v12"
              :zoom="10"
              @mb-click="onMapClick">
              <MapboxLayer id="champs-polygons"     :options="champsLayerOptions"/>
              <MapboxLayer id="champs-outlines"     :options="champsOutlineLayerOptions"/>
              <MapboxLayer id="champs-labels"       :options="champsLabelLayerOptions"/>
              <MapboxLayer id="champs-group-labels" :options="champsGroupLabelLayerOptions"/>
              <MapboxMarker :lng-lat="markerPos"/>
            </MapboxMap>
          </div>

          <!-- Producer filter overlay -->
          <div class="map-overlay-tl">
            <v-menu location="bottom start" :close-on-content-click="false">
              <template #activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  size="small"
                  icon
                  color="white"
                  style="background: rgba(0,0,0,0.55); backdrop-filter: blur(6px);"
                  title="Filtre producteurs">
                  <v-icon size="18">mdi-layers-outline</v-icon>
                </v-btn>
              </template>
              <v-card rounded="lg" elevation="8" style="min-width: 210px;">
                <div class="d-flex align-center justify-space-between px-3 pt-2 pb-1">
                  <span class="text-caption font-weight-bold text-uppercase" style="letter-spacing: 0.07em;">Producteurs</span>
                  <div class="d-flex" style="gap: 2px;">
                    <v-btn size="x-small" variant="text" @click="selectedProducers = [...producerColorMap.keys()]">Tous</v-btn>
                    <v-btn size="x-small" variant="text" @click="selectedProducers = []">Aucun</v-btn>
                  </div>
                </div>
                <v-divider/>
                <div class="d-flex align-center px-3 py-1" style="gap: 8px;">
                  <v-switch v-model="showChamps" density="compact" hide-details color="primary" style="flex-shrink:0;"/>
                  <span class="text-body-2">Afficher les champs</span>
                </div>
                <v-divider/>
                <div style="overflow-y: auto; max-height: 280px; padding: 4px 8px 8px;">
                  <div
                    v-for="[producer, color] in producerColorMap"
                    :key="producer"
                    class="d-flex align-center"
                    style="gap: 6px; padding: 1px 0;">
                    <v-checkbox
                      v-model="selectedProducers"
                      :value="producer"
                      density="compact"
                      hide-details
                      :color="color"
                      style="flex-shrink: 0;"/>
                    <span class="text-body-2" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 130px;">{{ producer }}</span>
                    <span :style="{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: '0', marginLeft: 'auto' }"/>
                  </div>
                </div>
              </v-card>
            </v-menu>
          </div>

          <div class="map-hint">
            <v-icon icon="mdi-cursor-default-click-outline" size="14" class="me-1"/>
            Cliquer pour choisir un point
          </div>
        </div>

      </div>
    </div>

    <v-divider/>

    <!-- Chart section -->
    <template v-if="items.length > 0 && !isLoading">
    <div class="section-header px-5 pt-4 pb-2">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
        <div class="d-flex align-center" style="gap: 10px;">
          <v-avatar color="success" variant="tonal" size="36" rounded="lg">
            <v-icon icon="mdi-chart-line" size="20"/>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2;">Graphique</div>
            <div class="text-caption text-medium-emphasis">Évolution journalière</div>
          </div>
        </div>
        <v-btn
          color="primary" variant="tonal" rounded="pill" size="small"
          prepend-icon="mdi-download"
          @click="exportCSV">
          Exportation CSV
        </v-btn>
      </div>
    </div>
    <div class="px-4 pb-4">
      <v-chart style="min-width: 200px; min-height: 340px;" :option="chartOptions" autoresize/>
    </div>

    <v-divider/>
    </template>

    <!-- Table section -->
    <div class="section-header px-5 pt-4 pb-2">
      <div class="d-flex align-center" style="gap: 10px;">
        <v-avatar color="warning" variant="tonal" size="36" rounded="lg">
          <v-icon icon="mdi-table" size="20"/>
        </v-avatar>
        <div>
          <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2;">Données brutes</div>
          <div class="text-caption text-medium-emphasis">
            {{ items.length }} entrée{{ items.length !== 1 ? 's' : '' }}
          </div>
        </div>
      </div>
    </div>
    <div class="px-4 pb-5">
      <v-data-table
        :headers="headers"
        :items="items"
        density="compact"
        :items-per-page="15"
        class="rounded-lg"
      />
    </div>

  </v-card>
</template>

<style scoped>
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

.section-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"');
}

.map-wrapper {
  height: 240px;
  border-radius: 12px;
  overflow: hidden;
  cursor: crosshair;
}

.map-overlay-tl {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
}

.map-hint {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 0.72rem;
  padding: 3px 10px;
  border-radius: 20px;
  pointer-events: none;
  white-space: nowrap;
  display: flex;
  align-items: center;
  backdrop-filter: blur(4px);
}
</style>
